import { 
  CalculationResults, 
  BoQItem, 
  BoQSection, 
  BoQSummary,
  TenderState 
} from '../types/tender.types';

export class BoQGenerator {
  /**
   * Generate Bill of Quantities matching screenshot format exactly
   */
  static generateBoQ(
    calculationResults: CalculationResults,
    tenderState: TenderState
  ): { sections: BoQSection[]; summary: BoQSummary } {
    const sections: BoQSection[] = [];

    // Create the main section as per screenshot: ROADS & SEWERS
    const mainSection = this.generateMainSection(calculationResults, tenderState);
    sections.push(mainSection);

    // Generate summary
    const summary = this.generateSummary(calculationResults);

    return { sections, summary };
  }

  /**
   * Generate main ROADS & SEWERS section matching screenshot exactly
   */
  private static generateMainSection(
    results: CalculationResults,
    _state: TenderState
  ): BoQSection {
    const items: BoQItem[] = [];

    // ROADS & SEWERS header
    items.push({
      id: 'roads-sewers-header',
      description: 'ROADS & SEWERS',
      amount: 0,
      category: 'ROADS & SEWERS',
      subCategory: '',
      isHeader: true,
      level: 0
    });

    // Empty row
    items.push({
      id: 'empty-1',
      description: '',
      amount: 0,
      category: '',
      subCategory: '',
      level: 0
    });

    // PRELIMINARIES
    items.push({
      id: 'preliminaries',
      description: 'PRELIMINARIES',
      amount: results.preliminaryItems.totalCost,
      category: 'ROADS & SEWERS',
      subCategory: 'PRELIMINARIES',
      level: 1,
      status: '',
      source: { moduleId: 'preliminaryItems', itemId: 'total' }
    });

    // Empty row
    items.push({
      id: 'empty-2',
      description: '',
      amount: 0,
      category: '',
      subCategory: '',
      level: 0
    });

    // FOUL SEWERS
    items.push({
      id: 'foul-sewers',
      description: 'FOUL SEWERS',
      amount: results.foulSewers.totalCost,
      category: 'ROADS & SEWERS',
      subCategory: 'FOUL SEWERS',
      level: 1,
      status: '',
      source: { moduleId: 'foulSewers', itemId: 'total' }
    });

    // Empty row
    items.push({
      id: 'empty-3',
      description: '',
      amount: 0,
      category: '',
      subCategory: '',
      level: 0
    });

    // STORM SEWERS
    items.push({
      id: 'storm-sewers',
      description: 'STORM SEWERS',
      amount: results.stormSewers.totalCost,
      category: 'ROADS & SEWERS',
      subCategory: 'STORM SEWERS',
      level: 1,
      status: '',
      source: { moduleId: 'stormSewers', itemId: 'total' }
    });

    // Empty row
    items.push({
      id: 'empty-4',
      description: '',
      amount: 0,
      category: '',
      subCategory: '',
      level: 0
    });

    // SAVINGS ITEMS (highlighted in yellow)
    items.push({
      id: 'saving-ultra-rib',
      description: 'SAVING FOR USING ULTRA RIB IN LIEU OF CLAY',
      amount: 0,
      category: 'ROADS & SEWERS',
      subCategory: 'SAVINGS',
      level: 1,
      isSavingsItem: true,
      isHighlighted: true,
      status: 'If Required',
      source: { moduleId: 'savings', itemId: 'ultra-rib' }
    });

    items.push({
      id: 'saving-as-dug',
      description: 'SAVING FOR AS-DUG BACKFILL TO SEWER TRENCHES IN SOFT LANDSCAPED LOCATIONS',
      amount: 0,
      category: 'ROADS & SEWERS',
      subCategory: 'SAVINGS',
      level: 1,
      isSavingsItem: true,
      isHighlighted: true,
      status: 'If Required',
      source: { moduleId: 'savings', itemId: 'as-dug' }
    });

    items.push({
      id: 'saving-recycled',
      description: 'SAVING FOR USING RECYCLED 6F2 AS SEWER BACKFILL IN LIEU OF TYPE 1',
      amount: 0,
      category: 'ROADS & SEWERS',
      subCategory: 'SAVINGS',
      level: 1,
      isSavingsItem: true,
      isHighlighted: true,
      status: 'If Required',
      source: { moduleId: 'savings', itemId: 'recycled-6f2' }
    });

    // S38 WORKS item
    items.push({
      id: 's38-works',
      description: 'S38 WORKS TO ACHIEVE 15%+ CBR',
      amount: 0,
      category: 'ROADS & SEWERS',
      subCategory: 'S38',
      level: 1,
      status: 'Rates Provided',
      source: { moduleId: 's38', itemId: 'cbr-works' }
    });

    // Empty row
    items.push({
      id: 'empty-5',
      description: '',
      amount: 0,
      category: '',
      subCategory: '',
      level: 0
    });

    // CAPPING LAYER items (highlighted in yellow)
    const cappingItems = [
      'EO FOR XXXmm CAPPING LAYER IN 6F2 (CBR X% - X%); STOCKPILE SURPLUS',
      'EO REMOVE SURPLUS ARISINGS FROM XXXmm CAPPING'
    ];

    cappingItems.forEach((desc, index) => {
      items.push({
        id: `capping-${index + 1}`,
        description: desc,
        amount: 0,
        category: 'ROADS & SEWERS',
        subCategory: 'CAPPING',
        level: 1,
        isHighlighted: true,
        status: 'If Required',
        source: { moduleId: 'capping', itemId: `item-${index + 1}` }
      });
    });

    // Repeat capping items (as shown in screenshot)
    cappingItems.forEach((desc, index) => {
      items.push({
        id: `capping-${index + 3}`,
        description: desc,
        amount: 0,
        category: 'ROADS & SEWERS',
        subCategory: 'CAPPING',
        level: 1,
        isHighlighted: true,
        status: 'If Required',
        source: { moduleId: 'capping', itemId: `item-${index + 3}` }
      });
    });

    // Another set of capping items
    cappingItems.forEach((desc, index) => {
      items.push({
        id: `capping-${index + 5}`,
        description: desc,
        amount: 0,
        category: 'ROADS & SEWERS',
        subCategory: 'CAPPING',
        level: 1,
        isHighlighted: true,
        status: 'If Required',
        source: { moduleId: 'capping', itemId: `item-${index + 5}` }
      });
    });

    // MAINS SERVICE TRENCHING
    items.push({
      id: 'mains-service',
      description: 'MAINS SERVICE TRENCHING',
      amount: 0,
      category: 'ROADS & SEWERS',
      subCategory: 'MAINS',
      level: 1,
      status: 'Rates Provided',
      source: { moduleId: 'mains', itemId: 'trenching' }
    });

    return {
      id: 'main-section',
      title: 'ROADS & SEWERS',
      items,
      subtotal: results.projectTotals.subtotal,
      level: 0
    };
  }

  /**
   * Generate summary section
   */
  private static generateSummary(results: CalculationResults): BoQSummary {
    return {
      labourTotal: results.projectTotals.breakdown.labour,
      materialTotal: results.projectTotals.breakdown.material,
      plantTotal: results.projectTotals.breakdown.plant,
      constructionSubtotal: results.projectTotals.subtotal,
      overhead: results.projectTotals.overhead,
      profit: results.projectTotals.profit,
      grandTotal: results.projectTotals.grandTotal,
      sections: {
        foulSewers: results.foulSewers.totalCost,
        stormSewers: results.stormSewers.totalCost,
        roads: results.roads.totalCost,
        preliminaryItems: results.preliminaryItems.totalCost
      }
    };
  }
}